import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PassphraseOptions, PasswordOptions, SEPARATORS,
  generatePassphrase, passphraseEntropy, passphraseWordlist,
  generatePassword, passwordEntropy,
} from '../generator/generate';

type Mode = 'passphrase' | 'password';

const strength = (bits: number) => {
  if (bits < 50) return { label: 'schwach', color: 'bg-red-500', text: 'text-red-400' };
  if (bits < 64) return { label: 'mittel', color: 'bg-amber-500', text: 'text-amber-400' };
  if (bits < 80) return { label: 'stark', color: 'bg-green-500', text: 'text-green-400' };
  return { label: 'sehr stark', color: 'bg-green-400', text: 'text-green-300' };
};

const Checkbox: React.FC<{ checked: boolean, onChange: (v: boolean) => void, label: string, hint?: string }> = ({ checked, onChange, label, hint }) => (
  <label className="flex items-start gap-3 cursor-pointer select-none">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 accent-indigo-500"
    />
    <span>
      <span className="text-sm text-slate-300">{label}</span>
      {hint && <span className="block text-xs text-slate-500">{hint}</span>}
    </span>
  </label>
);

const Slider: React.FC<{ label: string, value: number, min: number, max: number, onChange: (v: number) => void }> = ({ label, value, min, max, onChange }) => (
  <div className="space-y-2">
    <div className="flex justify-between text-sm">
      <span className="font-medium text-slate-300">{label}</span>
      <span className="font-mono text-indigo-300">{value}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-indigo-500"
    />
  </div>
);

const PasswordGenerator: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('passphrase');
  const [wordlist, setWordlist] = useState<string[] | null>(null);
  const [value, setValue] = useState('');
  const [copied, setCopied] = useState(false);

  const [phraseOpts, setPhraseOpts] = useState<PassphraseOptions>({
    words: 5, separator: '.', capitalize: true, addDigit: true, layoutSafe: true,
  });
  const [pwOpts, setPwOpts] = useState<PasswordOptions>({
    length: 20, upper: true, lower: true, digits: true, symbols: true, avoidAmbiguous: true, layoutSafe: true,
  });

  // The word list is ~60 KB, so it is only loaded on this page
  useEffect(() => {
    import('../generator/wordlist-de').then(module => setWordlist(module.default));
  }, []);

  // A separator that is not layout-safe falls back to '.' when the option is switched on
  useEffect(() => {
    if (phraseOpts.layoutSafe && !SEPARATORS.some(s => s.value === phraseOpts.separator && s.layoutSafe)) {
      setPhraseOpts(o => ({ ...o, separator: '.' }));
    }
  }, [phraseOpts.layoutSafe, phraseOpts.separator]);

  const regenerate = useCallback(() => {
    if (mode === 'passphrase') {
      if (wordlist) setValue(generatePassphrase(wordlist, phraseOpts));
    } else {
      setValue(generatePassword(pwOpts));
    }
    setCopied(false);
  }, [mode, wordlist, phraseOpts, pwOpts]);

  useEffect(regenerate, [regenerate]);

  const bits = mode === 'passphrase'
    ? (wordlist ? passphraseEntropy(passphraseWordlist(wordlist, phraseOpts.layoutSafe).length, phraseOpts) : 0)
    : passwordEntropy(pwOpts);
  const s = strength(bits);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    // Passed in memory via router state; the share page clears it after reading
    navigate('/', { state: { prefill: value } });
  };

  const setPhrase = (patch: Partial<PassphraseOptions>) => setPhraseOpts(o => ({ ...o, ...patch }));
  const setPw = (patch: Partial<PasswordOptions>) => setPwOpts(o => ({ ...o, ...patch }));

  const tabClass = (active: boolean) => `flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
    active ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'
  }`;

  return (
    <div className="glass-panel p-8 rounded-2xl shadow-2xl space-y-6">
      {/* Mode Selector */}
      <div className="flex gap-2 p-1 bg-slate-900/50 rounded-xl border border-slate-700">
        <button type="button" onClick={() => setMode('passphrase')} className={tabClass(mode === 'passphrase')}>
          Passphrase
        </button>
        <button type="button" onClick={() => setMode('password')} className={tabClass(mode === 'password')}>
          Passwort
        </button>
      </div>

      {/* Result */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <div
            data-testid="generated"
            className={`flex-1 min-w-0 bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-4 font-mono text-lg text-white select-all ${
              mode === 'passphrase' ? 'break-words' : 'break-all'
            }`}
          >
            {value && mode === 'passphrase' && phraseOpts.separator
              // Allow line breaks only after a separator, never inside a word
              ? value.split(phraseOpts.separator).reduce<React.ReactNode[]>((parts, word, i) =>
                  i === 0 ? [word] : [...parts, phraseOpts.separator, <wbr key={i} />, word], [])
              : value ||<span className="text-slate-600">{wordlist || mode === 'password' ? 'Bitte Optionen wählen' : 'Lade Wortliste...'}</span>}
          </div>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!value}
              title="Kopieren"
              className={`px-3 py-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 ${
                copied ? 'bg-green-600 text-white' : 'bg-indigo-600 hover:bg-indigo-500 text-white'
              }`}
            >
              {copied ? 'Kopiert!' : 'kopieren'}
            </button>
            <button
              type="button"
              onClick={regenerate}
              title="Neu erzeugen"
              className="px-3 py-2 rounded-lg font-medium text-sm bg-slate-800 hover:bg-slate-700 text-slate-200 transition-all"
            >
              neu
            </button>
          </div>
        </div>
        {value && (
          <div className="space-y-1">
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div className={`h-full ${s.color} transition-all`} style={{ width: `${Math.min(bits / 100, 1) * 100}%` }} />
            </div>
            <div className="flex justify-between text-xs">
              <span className={s.text}>{s.label} · ~{Math.floor(bits)} bit</span>
              <span className="text-slate-500">{value.length} Zeichen</span>
            </div>
          </div>
        )}
      </div>

      {/* Options */}
      {mode === 'passphrase' ? (
        <div className="space-y-5">
          <Slider label="Wörter" value={phraseOpts.words} min={3} max={10} onChange={words => setPhrase({ words })} />
          <div className="space-y-2">
            <label htmlFor="separator" className="block text-sm font-medium text-slate-300">Trennzeichen</label>
            <select
              id="separator"
              value={phraseOpts.separator}
              onChange={(e) => setPhrase({ separator: e.target.value })}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {SEPARATORS.filter(sep => sep.layoutSafe || !phraseOpts.layoutSafe).map(sep => (
                <option key={sep.value} value={sep.value}>{sep.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-3">
            <Checkbox checked={phraseOpts.capitalize} onChange={capitalize => setPhrase({ capitalize })} label="Wörter großschreiben" />
            <Checkbox checked={phraseOpts.addDigit} onChange={addDigit => setPhrase({ addDigit })} label="Eine Ziffer einfügen" />
            <Checkbox
              checked={phraseOpts.layoutSafe}
              onChange={layoutSafe => setPhrase({ layoutSafe })}
              label="Tastaturlayout-sicher"
              hint="Tippt sich auf deutscher und US-Tastatur gleich: keine Wörter mit y/z, nur passende Trennzeichen."
            />
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <Slider label="Länge" value={pwOpts.length} min={8} max={64} onChange={length => setPw({ length })} />
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Checkbox checked={pwOpts.upper} onChange={upper => setPw({ upper })} label="A–Z" />
              <Checkbox checked={pwOpts.lower} onChange={lower => setPw({ lower })} label="a–z" />
              <Checkbox checked={pwOpts.digits} onChange={digits => setPw({ digits })} label="0–9" />
              <Checkbox checked={pwOpts.symbols} onChange={symbols => setPw({ symbols })} label={pwOpts.layoutSafe ? 'Sonderzeichen  ! $ % .' : 'Sonderzeichen'} />
            </div>
            <Checkbox checked={pwOpts.avoidAmbiguous} onChange={avoidAmbiguous => setPw({ avoidAmbiguous })} label="Ähnliche Zeichen vermeiden" hint="Ohne I, l, 1, O, 0, o" />
            <Checkbox
              checked={pwOpts.layoutSafe}
              onChange={layoutSafe => setPw({ layoutSafe })}
              label="Tastaturlayout-sicher"
              hint="Tippt sich auf deutscher und US-Tastatur gleich: ohne y/z, nur Sonderzeichen auf gleicher Taste."
            />
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleShare}
        disabled={!value}
        className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-600/50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl shadow-lg shadow-indigo-500/20 transition-all active:scale-[0.98]"
      >
        Sicher teilen →
      </button>
    </div>
  );
};

export default PasswordGenerator;
